import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import Header from '../Header';
import AdminNav from './AdminNav';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
    { value: '', label: 'Todas' },
    { value: 'WAITING', label: 'En espera' },
    { value: 'STARTING', label: 'Iniciando' },
    { value: 'PLAYING', label: 'Jugando' },
    { value: 'PAUSED', label: 'Pausada' },
    { value: 'FINISHED', label: 'Finalizada (histórica)' },
    { value: 'CANCELLED', label: 'Cancelada' },
];

const STATUS_COLOR = {
    WAITING: 'info',
    STARTING: 'info',
    PLAYING: 'success',
    PAUSED: 'warning',
    FINISHED: 'default',
    CANCELLED: 'error',
};

const ACTIVE_STATUSES = new Set(['WAITING', 'STARTING', 'PLAYING', 'PAUSED']);

export default function AdminRoomsScreen() {
    const { authFetch } = useAuth();

    const [status, setStatus] = useState('');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const [detailRoomId, setDetailRoomId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [actions, setActions] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const load = useCallback(
        async (offset = 0) => {
            setError(null);
            setIsLoading(true);
            try {
                const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
                if (status) params.set('status', status);
                const result = await authFetch(`/admin/rooms?${params.toString()}`);
                if (offset === 0) setItems(result.items);
                else setItems((prev) => [...prev, ...result.items]);
                setTotal(result.total);
            } catch (err) {
                setError(err instanceof ApiError ? err.message : 'No se pudo cargar la lista de salas.');
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, status]
    );

    useEffect(() => {
        load(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const openDetail = async (roomId) => {
        setDetailRoomId(roomId);
        setDetail(null);
        setActions(null);
        setDetailLoading(true);
        try {
            const [detailResult, actionsResult] = await Promise.all([
                authFetch(`/admin/rooms/${roomId}`),
                authFetch(`/admin/rooms/${roomId}/actions?limit=20`),
            ]);
            setDetail(detailResult);
            setActions(actionsResult);
        } catch (err) {
            setDetail({ error: err instanceof ApiError ? err.message : 'No se pudo cargar el detalle.' });
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCancel = async (roomId) => {
        if (!window.confirm('¿Cancelar esta sala? Esta acción termina la partida en curso si la hay.')) return;
        setBusyId(roomId);
        setError(null);
        try {
            await authFetch(`/admin/rooms/${roomId}/cancel`, { method: 'POST' });
            setItems((prev) => prev.map((r) => (r.id === roomId ? { ...r, status: 'CANCELLED' } : r)));
            if (detailRoomId === roomId) openDetail(roomId);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'No se pudo cancelar la sala.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <>
            <Header title="Partidas" showSettings={false} />
            <AdminNav />
            <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
                <TextField
                    select
                    label="Estado"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    size="small"
                    sx={{ mb: 2, minWidth: 220 }}
                >
                    {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TableContainer sx={{ bgcolor: 'background.paper' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Código</TableCell>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Juego</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Marcador</TableCell>
                                <TableCell>Creada</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell onClick={() => openDetail(r.id)} sx={{ cursor: 'pointer', fontFamily: 'monospace' }}>{r.code}</TableCell>
                                    <TableCell onClick={() => openDetail(r.id)} sx={{ cursor: 'pointer' }}>{r.name}</TableCell>
                                    <TableCell>{r.gameType} / {r.gameMode}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={r.status} color={STATUS_COLOR[r.status] || 'default'} />
                                    </TableCell>
                                    <TableCell>{r.matchScoreTeam1} - {r.matchScoreTeam2}</TableCell>
                                    <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            color="error"
                                            disabled={busyId === r.id || !ACTIVE_STATUSES.has(r.status)}
                                            onClick={() => handleCancel(r.id)}
                                        >
                                            Cancelar
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!isLoading && items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Sin resultados.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && items.length < total && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button onClick={() => load(items.length)}>Cargar más ({items.length}/{total})</Button>
                    </Box>
                )}
            </Container>

            <Dialog open={Boolean(detailRoomId)} onClose={() => setDetailRoomId(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Sala {detail?.room?.code}</DialogTitle>
                <DialogContent dividers>
                    {detailLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {detail?.error && <Alert severity="error">{detail.error}</Alert>}
                    {detail && !detail.error && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2">Estado</Typography>
                                <Chip size="small" label={detail.room.status} color={STATUS_COLOR[detail.room.status] || 'default'} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2">Sesión en vivo</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {detail.liveSession
                                        ? `versión ${detail.liveSession.version} · asientos conectados: ${detail.liveSession.connectedSeats.join(', ') || 'ninguno'}`
                                        : 'Sin sesión en vivo.'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2">Asientos</Typography>
                                <List dense>
                                    {detail.room.seats.map((s) => (
                                        <ListItem key={s.seat} disableGutters>
                                            <ListItemText
                                                primary={`#${s.seat} · ${s.userName || '(vacío)'}`}
                                                secondary={`${s.occupantType} · equipo ${s.team}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                            {actions && (
                                <Box>
                                    <Typography variant="subtitle2">Últimas acciones ({actions.total})</Typography>
                                    <List dense sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {actions.items.map((a, idx) => (
                                            <ListItem key={a.id ?? idx} disableGutters>
                                                <ListItemText
                                                    primary={a.actionType || a.type || JSON.stringify(a)}
                                                    secondary={a.createdAt ? new Date(a.createdAt).toLocaleString() : undefined}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    {detail?.room && ACTIVE_STATUSES.has(detail.room.status) && (
                        <Button color="error" onClick={() => handleCancel(detail.room.id)}>Cancelar sala</Button>
                    )}
                    <Button onClick={() => setDetailRoomId(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
